import { map, find, invertObj, assoc, mapObjIndexed, values } from 'ramda';

/*
 * Category = {
 *     canonicalName    : String   // value of category - never changes
 *     hasBadge         : Boolean  // if category has a badge available
 *     internalOnly     : Boolean  // if this is for internal use only
 *     label            : String   // for dropdown - can change depending on region
 *     v4CanonicalNames : [String] // v4 icons
 *     venueId          : String   // the venue that uses this category
 * }
 */

/*
 * Return a dropdown select option from a Category
 * @sig asDropdownOption :: Category -> ({ label: String, value: String })
 */
const asDropdownOption = ({ canonicalName, label }) => ({ label, value: canonicalName });

/*
 * Return a list of Categories as dropdown options
 * @sig asDropdownOptions :: [Category] -> [ ({ label: String, value: String }) ]
 */
const asDropdownOptions = categories => map(asDropdownOption, categories);

/*
 * Return the name for this category
 * @sig getName :: Category -> String
 */
const getName = category => category.canonicalName;

/*
 * Return the name for this category
 * @sig getName :: Category -> String
 */
const getV4Names = category => category.v4CanonicalNames;

/*
 * Return the url for the badge associated with this category
 * @sig getUrlFromCategory :: Category -> String
 */
const getUrlFromCategory = category => category.badge;

/*
 * Return a v5 dropdown select option from a Category and a Badge
 * createBadgeDropdown :: (Category, Badge) -> { badge, label, value }
 * Add the badge url here since venue data might not have loaded when we initially enrich categories so it may default to v5.
 */
const createBadgeDropdown = (category, venueCategory, venueId, accountId) => {
    return {
        badge: `https://content.locuslabs.com/${venueCategory}/badge/${venueId}/${accountId}/${category.canonicalName}.svg`,
        label: category.label,
        value: category.canonicalName,
    };
};

/*
 * Return a v4 dropdown select option from a Category and a Badge
 * Add the badge url here since venue data might not have loaded when we initially enrich categories so it may default to v5.
 * @sig v4Dropdown :: [String, Badge] -> ({ label: String, value: String, badge: Badge })
 */
const v4Dropdown = canonicalName => {
    const label = canonicalName.replace('v4', '');
    return {
        badge: `https://content.locuslabs.com/vms-ui/category-icons/${canonicalName}.svg`,
        label,
        value: canonicalName,
    };
};

/*
 * createV4BadgeDropdown :: ([String], [Badge]) -> { badge, label, value }
 */
const createV4BadgeDropdown = v4CanonicalNames => map(v4Dropdown, v4CanonicalNames);

/*
 * Returns a category - for use with bulk editing, when there is no category selected
 * @sig fromCategoryList :: (String, [Category]) -> Category
 */
const fromCategoryList = (canonicalName, categories) => {
    const category = find(c => c.canonicalName === canonicalName, categories) || null;
    return category;
};

/*
 * Given a querywin string, return the key that has the querywin string as its value
 * @sig fromV4IconToValue :: String -> String
 */
const fromV4IconToValue = icon => invertObj(V4_BADGE_MAP)[icon];

/*
 * Given a v4 Category value, return a querywin string
 * @sig fromV4CategoryValueToIcon :: String -> String
 */
const fromV4CategoryToIcon = (v4Category, updates) => {
    const { v4CanonicalNames } = v4Category;

    if (v4CanonicalNames.length === 1) updates = assoc('icon', V4_BADGE_MAP[v4CanonicalNames[0]], updates);

    return updates;
};

/*
 * Given a v5 Category value, return a querywin string
 * @sig fromV5CategoryValueToIcon :: String -> String
 */
const fromV5CategoryToIcon = updates => assoc('icon', V5_BADGE_MAP[updates.category], updates);

/*
 * Given an icon value, get the icon from the badge map
 * @sig getIconFromV4Value :: String -> String
 */
const getIconFromV4Value = v4Value => V4_BADGE_MAP[v4Value];

const toCategory = venueId => (categoryData, canonicalName) => {
    const topLevelCategory = canonicalName.includes('.') ? canonicalName.split('.')[0] : canonicalName;
    return {
        canonicalName,
        hasBadge: categoryData.hasBadge,
        internalOnly: categoryData.internalOnly,
        label: canonicalName,
        v4CanonicalNames: V4[topLevelCategory] || [''],
        venueId,
    };
};

const getCategoryFromTheme = (venueId, canonicalName, theme) => {
    const category = theme ? theme[canonicalName] : null;

    return category ? toCategory(venueId)(category, canonicalName) : {};
};

/*
 * Transforms raw category data into a Category
 * @sig fromData :: (String -> URL) -> Object -> Category
 */
const fromTheme = (venueId, theme) => {
    const categories = mapObjIndexed(toCategory(venueId), theme);

    return values(categories);
};

/*
 * Current v4 top level category options list. Temporary.
 * Remove once stored elsewhere / all maps are v5
 */
const V4 = {
    art: ['v4services'],
    baggage: ['v4bagclaim', 'v4baggage-wrap'],
    bus: ['v4bus'],
    door: ['v4exit'],
    'check-in': ['v4ticketing'],
    eat: ['v4eat', 'v4vending'],
    elevator: ['v4elevator'],
    escalator: ['v4escalator'],
    gate: ['v4gate'],
    parking: ['v4parking'],
    power: ['v4charging'],
    ramp: ['v4ramp'],
    relax: ['v4prayerroom', 'v4relax', 'v4tv'],
    restroom: ['v4restroom', 'v4restroommen', 'v4restroomwomen'],
    rideshare: ['v4car', 'v4uber'],
    services: [
        'v4aed',
        'v4airportinfo',
        'v4atm',
        'v4bagcart',
        'v4casino',
        'v4currencyexch-dollar',
        'v4currencyexch-euro',
        'v4mailbox',
        'v4medical',
        'v4movierental',
        'v4newspaper',
        'v4nursing',
        'v4petrelief',
        'v4police',
        'v4services',
        'v4shoeshine',
        'v4smoking',
        'v4valet',
        'v4wheelchair',
    ],
    shop: ['v4shop'],
    stairs: ['v4stairs'],
    terminal: ['v4gate'],
    train: ['v4train'],
    transport: ['v4bus', 'v4car', 'v4exit', 'v4ramp', 'v4security', 'v4train', 'v4walkway'],
    water: ['v4drinking-fountain'],
};

const V4_BADGE_MAP = {
    v4aed: 'querywin-badge-aed@2x.png',
    v4airportinfo: 'querywin-icon-airportinfo@2x.png',
    v4atm: 'querywin-icon-atm@2x.png',
    v4bagcart: 'querywin-icon-bagcart@2x.png',
    v4bagclaim: 'querywin-icon-bagclaim@2x.png',
    'v4baggage-wrap': 'querywin-badge-baggage-wrap@2x.png',
    v4bus: 'querywin-icon-bus@2x.png',
    v4car: 'querywin-icon-car@2x.png',
    v4casino: 'querywin-icon-casino@2x.png',
    v4charging: 'querywin-icon-charging@2x.png',
    'v4currencyexch-dollar': 'querywin-icon-currencyexch-dollar@2x.png',
    'v4currencyexch-euro': 'querywin-icon-currencyexch-euro@2x.png',
    'v4drinking-fountain': 'querywin-badge-drinking-fountain@2x.png',
    v4eat: 'querywin-icon-eat@2x.png',
    v4elevator: 'querywin-icon-elevator@2x.png',
    v4escalator: 'querywin-icon-escalator@2x.png',
    v4exit: 'querywin-icon-exit@2x.png',
    v4gate: 'querywin-icon-gate@2x.png',
    v4mailbox: 'querywin-icon-mailbox@2x.png',
    v4medical: 'querywin-icon-medical@2x.png',
    v4movierental: 'querywin-icon-movierental@2x.png',
    v4newspaper: 'querywin-badge-newspaper@2x.png',
    v4nursing: 'querywin-badge-nursing@2x.png',
    v4parking: 'querywin-icon-parking@2x.png',
    v4petrelief: 'querywin-badge-petrelief@2x.png',
    v4police: 'querywin-icon-police@2x.png',
    v4prayerroom: 'querywin-icon-prayerroom@2x.png',
    v4ramp: 'querywin-icon-ramp@2x.png',
    v4relax: 'querywin-icon-relax@2x.png',
    v4restroom: 'querywin-icon-restroom@2x.png',
    v4restroommen: 'querywin-icon-restroommen@2x.png',
    v4restroomwomen: 'querywin-icon-restroomwomen@2x.png',
    v4security: 'querywin-icon-security@2x.png',
    v4services: 'querywin-icon-services@2x.png',
    v4shoeshine: 'querywin-icon-shoeshine@2x.png',
    v4shop: 'querywin-icon-shop@2x.png',
    v4smoking: 'querywin-icon-smoking@2x.png',
    v4stairs: 'querywin-icon-stairs@2x.png',
    v4ticketing: 'querywin-icon-ticketing@2x.png',
    v4train: 'querywin-icon-train@2x.png',
    v4tv: 'querywin-badge-tv@2x.png',
    v4uber: 'querywin-badge-uber@2x.png',
    v4valet: 'querywin-icon-valet@2x.png',
    v4vending: 'querywin-icon-vending@2x.png',
    v4walkway: 'querywin-icon-walkway@2x.png',
    v4wheelchair: 'querywin-icon-wheelchair@2x.png',
};

const V5_BADGE_MAP = {
    art: 'querywin-icon-services@2x.png',
    baggage: 'querywin-icon-bagclaim@2x.png',
    'baggage.cart': 'querywin-icon-bagcart@2x.png',
    'baggage.claim': 'querywin-icon-bagclaim@2x.png',
    'baggage.drop': 'querywin-icon-bagclaim@2x.png',
    'baggage.office': 'querywin-icon-bagclaim@2x.png',
    'baggage.oversize': 'querywin-icon-bagclaim@2x.png',
    'baggage.wrap': 'querywin-icon-bagclaim@2x.png',
    checkin: 'querywin-icon-ticketing@2x.png',
    'checkin.curbside': 'querywin-icon-ticketing@2x.png',
    eat: 'querywin-icon-eat@2x.png',
    'eat.bar': 'querywin-icon-eat@2x.png',
    'eat.coffee': 'querywin-icon-eat@2x.png',
    'eat.vending': 'querywin-icon-eat@2x.png',
    gate: 'querywin-icon-gate@2x.png',
    'gate.arrival': 'querywin-icon-gate@2x.png',
    'gate.departures': 'querywin-icon-gate@2x.png',
    parking: 'querywin-icon-parking@2x.png',
    'parking.accessible': 'querywin-icon-parking@2x.png',
    'parking.ev': 'querywin-icon-parking@2x.png',
    portal: 'querywin-icon-elevator@2x.png',
    'portal.bus': 'querywin-icon-bus@2x.png',
    'portal.train': 'querywin-icon-train@2x.png',
    'portal.elevator': 'querywin-icon-elevator@2x.png',
    'portal.escalator': 'querywin-icon-escalator@2x.png',
    'portal.movingwalkway': 'querywin-icon-walkway@2x.png',
    'portal.ramp': 'querywin-icon-walkway@2x.png',
    'portal.stairs': 'querywin-icon-stairs@2x.png',
    relax: 'querywin-icon-relax@2x.png',
    'relax.chair': 'querywin-icon-relax@2x.png',
    'relax.chairdetail': 'querywin-icon-relax@2x.png',
    'relax.childrensplayarea': 'querywin-icon-relax@2x.png',
    'relax.lounge': 'querywin-icon-relax@2x.png',
    'relax.prayerroom': 'querywin-icon-relax@2x.png',
    'relax.seating': 'querywin-icon-relax@2x.png',
    'relax.tv': 'querywin-icon-relax@2x.png',
    restroom: 'querywin-icon-restroom@2x.png',
    'restroom.family': 'querywin-icon-restroom@2x.png',
    'restroom.female': 'querywin-icon-restroomwomen@2x.png',
    'restroom.male': 'querywin-icon-restroommen@2x.png',
    'restroom.unisex': 'querywin-icon-restroom@2x.png',
    security: 'querywin-icon-security@2x.png',
    'security.agricultureinspection': 'querywin-icon-security@2x.png',
    'security.checkpoint': 'querywin-icon-security@2x.png',
    'security.customs': 'querywin-icon-security@2x.png',
    'security.employee': 'querywin-icon-security@2x.png',
    'security.exit': 'querywin-icon-exit@2x.png',
    services: 'querywin-icon-services@2x.png',
    'services.aed': 'querywin-icon-services@2x.png',
    'services.animalreliefarea': 'querywin-icon-services@2x.png',
    'services.atm': 'querywin-icon-atm@2x.png',
    'services.bank': 'querywin-icon-services@2x.png',
    'services.chargingstation': 'querywin-icon-services@2x.png',
    'services.currencyexchange': 'querywin-icon-services@2x.png',
    'services.currencyexchange.dollar': 'querywin-icon-services@2x.png',
    'services.currencyexchange.euro': 'querywin-icon-services@2x.png',
    'services.currencyexchange.pound': 'querywin-icon-services@2x.png',
    'services.directory': 'querywin-icon-airportinfo@2x.png',
    'services.drinkingfountain': 'querywin-icon-services@2x.png',
    'services.firstaid': 'querywin-icon-services@2x.png',
    'services.information': 'querywin-icon-airportinfo@2x.png',
    'services.lottery': 'querywin-icon-services@2x.png',
    'services.mailbox': 'querywin-icon-services@2x.png',
    'services.mobilityassistance': 'querywin-icon-wheelchair@2x.png',
    'services.mothersroom': 'querywin-icon-services@2x.png',
    'services.movie': 'querywin-icon-movierental@2x.png',
    'services.newspaper': 'querywin-icon-services@2x.png',
    'services.police': 'querywin-icon-services@2x.png',
    'services.prepaidcard': 'querywin-icon-services@2x.png',
    'services.rentalcard': 'querywin-icon-services@2x.png',
    'services.shoeshine': 'querywin-icon-shoeshine@2x.png',
    'services.smokingarea': 'querywin-icon-smoking@2x.png',
    'services.specialservices': 'querywin-icon-services@2x.png',
    'services.waterrefill': 'querywin-icon-services@2x.png',
    shop: 'querywin-icon-shop@2x.png',
    ticketing: 'querywin-icon-ticketing@2x.png',
    'ticketing.selfcheckinkiosk': 'querywin-icon-ticketing@2x.png',
    transport: 'querywin-icon-train@2x.png',
    'transport.passengerpickup': 'querywin-icon-car@2x.png',
    'transport.sharedride': 'querywin-icon-car@2x.png',
    'transport.shuttle': 'querywin-icon-bus@2x.png',
    'transport.shuttle.public': 'querywin-icon-bus@2x.png',
    'transport.shuttle.secure': 'querywin-icon-bus@2x.png',
    'transport.taxi': 'querywin-icon-car@2x.png',
    'transport.train': 'querywin-icon-train@2x.png',
    'transport.valet': 'querywin-icon-car@2x.png',
};

export {
    asDropdownOptions,
    createBadgeDropdown,
    createV4BadgeDropdown,
    fromCategoryList,
    fromV4IconToValue,
    fromV4CategoryToIcon,
    fromV5CategoryToIcon,
    getIconFromV4Value,
    getName,
    getUrlFromCategory,
    getV4Names,
    fromTheme,
    toCategory,
    getCategoryFromTheme,
};
